"use client"

import { type ReactNode, useMemo } from 'react'
import { Award, Bell, Kanban, Medal, Trophy } from 'lucide-react'

import { PersonListCard } from '@/components/person-list-card'
import { PersonListCardSummary } from '@/components/person-list-card-summary'
import { MatchBandBadge } from '@/components/evaluation/match-band-badge'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { CardOpenControl } from '@/components/ui/card-open-control'
import { ListCardTrailingActionsFooter } from '@/components/layout/list-card-trailing-actions'
import type { UnifiedCandidate } from '@/hooks/use-smart-search'
import type { ApplicantCard, ApplicantProfileSummary } from '@/hooks/use-opportunity-applicants'
import type { OpenPersonMatchHandler } from '@/lib/evaluation-analysis-paths'
import type { OpenJobWorkspacePersonHandler } from '@/lib/job-workspace-person-paths'
import {
  candidateMatchToPersonModel,
  personDescriptionFromProfile,
} from '@/lib/person-list-card-model'
import { candidateScore, leaderboardCandidates } from '@/lib/smart-analysis-leaderboard'
import {
  displayableAnalysisHeadline,
  publicQualityAsCandidateBand,
  publicQualityFromEmployerScore,
} from '@/lib/ranking-vocabulary'
import { formatRelativeTimeIso, calendarDaysSinceIso } from '@/lib/time-format'
import { cn } from '@/lib/utils'

function matchQualityChip(score: number) {
  const quality = publicQualityFromEmployerScore(score)
  return {
    band: publicQualityAsCandidateBand(quality),
    percentLabel: `${(score * 100).toFixed(0)}%`,
  }
}

const RANK_ICONS = [Trophy, Medal, Award] as const

function rankIconForPosition(position: number) {
  return RANK_ICONS[position] ?? null
}

function candidateDisplayName(candidate: UnifiedCandidate): string {
  return candidate.name ?? candidate.full?.candidate_name ?? 'Candidate'
}

function candidateRawAnalysisHeadline(candidate: UnifiedCandidate): string | null {
  return candidate.full?.analysis_headline ?? candidate.full?.llm_reasoning ?? null
}

function resolveLeaderboardOpenPersonHandler(
  candidate: UnifiedCandidate,
  name: string,
  onOpenPerson?: OpenJobWorkspacePersonHandler | OpenPersonMatchHandler,
): (() => void) | undefined {
  if (!onOpenPerson) return undefined
  const full = candidate.full
  if (full) {
    return () => onOpenPerson(full.profile_id, full.candidate_name, full.evaluation_id)
  }
  return () => onOpenPerson(candidate.profile_id, name, undefined)
}

function InvitedNudgeAction({
  applicant,
  onNudge,
  nudgePending,
}: {
  applicant: ApplicantCard
  onNudge?: (applicationId: string) => void
  nudgePending?: boolean
}) {
  if (applicant.nudged_at) {
    return (
      <span className="text-sm text-muted-foreground">
        Nudged {formatRelativeTimeIso(applicant.nudged_at)}
      </span>
    )
  }

  if (!onNudge) return null

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      disabled={nudgePending}
      onClick={() => onNudge(applicant.id)}
    >
      <Bell className="mr-1.5 shrink-0" aria-hidden />
      Nudge
    </Button>
  )
}

export type SmartAnalysisLeaderboardProps = {
  candidates: UnifiedCandidate[]
  variant?: 'standalone' | 'job-report'
  onOpenPerson?: OpenJobWorkspacePersonHandler | OpenPersonMatchHandler
  applicantProfileById?: Record<string, ApplicantProfileSummary>
  applicantByProfileId?: Record<string, ApplicantCard>
  renderCandidateAction?: (candidate: UnifiedCandidate) => ReactNode
  onViewInPipeline?: (profileId: string) => void
  onNudge?: (applicationId: string) => void
  nudgePending?: boolean
  className?: string
}

function LeaderboardCandidateRow({
  candidate,
  position,
  onOpenPerson,
  applicantProfileById,
  renderCandidateAction,
  variant = 'standalone',
  applicant,
  onViewInPipeline,
  onNudge,
  nudgePending,
}: {
  candidate: UnifiedCandidate
  position: number
  onOpenPerson?: OpenJobWorkspacePersonHandler | OpenPersonMatchHandler
  applicantProfileById?: Record<string, ApplicantProfileSummary>
  renderCandidateAction?: (candidate: UnifiedCandidate) => ReactNode
  variant?: 'standalone' | 'job-report'
  applicant?: ApplicantCard
  onViewInPipeline?: (profileId: string) => void
  onNudge?: (applicationId: string) => void
  nudgePending?: boolean
}) {
  const rank = position + 1
  const RankIcon = rankIconForPosition(position)
  const profileSummary = applicantProfileById?.[candidate.profile_id]
  const full = candidate.full
  const score = candidateScore(candidate)
  const qualityChip = matchQualityChip(score)
  const rawAnalysisHeadline = candidateRawAnalysisHeadline(candidate)
  const headline = displayableAnalysisHeadline(rawAnalysisHeadline)
  const name = candidateDisplayName(candidate)
  const personModel = full
    ? candidateMatchToPersonModel(full, profileSummary)
    : {
        id: candidate.profile_id,
        name,
        avatarUrl: profileSummary?.avatar_url ?? null,
        colorSeed: candidate.profile_id,
      }

  const isInvited = applicant?.status === 'invited'
  const jobReportAction =
    variant === 'job-report' ? (
      <div className="flex flex-wrap items-center gap-2">
        {isInvited && applicant ? (
          <InvitedNudgeAction applicant={applicant} onNudge={onNudge} nudgePending={nudgePending} />
        ) : (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onViewInPipeline?.(candidate.profile_id)}
          >
            <Kanban className="mr-1.5 shrink-0" aria-hidden />
            View in pipeline
          </Button>
        )}
      </div>
    ) : null

  const footerInner = jobReportAction ?? renderCandidateAction?.(candidate)
  const footerContent = footerInner ? (
    <ListCardTrailingActionsFooter>{footerInner}</ListCardTrailingActionsFooter>
  ) : undefined

  const statusLabel =
    variant === 'job-report' && applicant
      ? isInvited
        ? `Invited · awaiting application · ${calendarDaysSinceIso(applicant.applied_at)}d`
        : applicant.current_stage?.name ?? 'In pipeline'
      : null

  const standaloneBody =
    variant === 'standalone' ? (
      <div className="space-y-2">
        <PersonListCardSummary
          analysisHeadline={rawAnalysisHeadline}
          description={personDescriptionFromProfile(profileSummary)}
          skills={profileSummary?.top_skills}
          maxSkills={4}
        />
        {full ? (
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
            <span>
              <span className="text-muted-foreground">Structured: </span>
              <span className="font-medium text-foreground">
                {(full.structured_score * 100).toFixed(0)}%
              </span>
            </span>
            <span>
              <span className="text-muted-foreground">Semantic: </span>
              <span className="font-medium text-foreground">
                {(full.semantic_score * 100).toFixed(0)}%
              </span>
            </span>
            {full.llm_score != null ? (
              <span>
                <span className="text-muted-foreground">LLM: </span>
                <span className="font-medium text-foreground">
                  {(full.llm_score * 100).toFixed(0)}%
                </span>
              </span>
            ) : null}
          </div>
        ) : null}
      </div>
    ) : null

  const bodyContent =
    variant === 'job-report'
      ? headline
        ? <PersonListCardSummary analysisHeadline={rawAnalysisHeadline} />
        : undefined
      : standaloneBody

  return (
    <PersonListCard
      variant="evaluation"
      person={personModel}
      className={cn('border-border/80', isInvited && 'border-dashed')}
      onOpen={resolveLeaderboardOpenPersonHandler(candidate, name, onOpenPerson)}
      openAriaLabel={onOpenPerson ? `Open match analysis for ${name}` : undefined}
      headerAside={
        <div className="flex shrink-0 items-center gap-1.5 self-start">
          {RankIcon ? <RankIcon className="size-4 text-foreground" aria-hidden /> : null}
          <Badge variant="default" className="tabular-nums">
            #{rank}
          </Badge>
        </div>
      }
      meta={
        <div className="flex flex-wrap items-center gap-2">
          <MatchBandBadge band={qualityChip.band} />
          <span className="text-xs tabular-nums text-muted-foreground">{qualityChip.percentLabel}</span>
          {statusLabel ? (
            <span
              className={cn(
                'text-xs',
                isInvited ? 'font-medium text-warning' : 'text-muted-foreground',
              )}
            >
              {statusLabel}
            </span>
          ) : null}
          {variant === 'standalone' && full?.was_llm_judged ? (
            <Badge variant="outline" className="text-xs">
              LLM Evaluated
            </Badge>
          ) : null}
        </div>
      }
      body={bodyContent}
      footer={footerContent}
      footerClassName={footerContent ? 'pointer-events-auto px-0' : undefined}
    />
  )
}

function CompactLeaderboardRow({
  candidate,
  position,
  applicant,
  onOpenPerson,
  onViewInPipeline,
  onNudge,
  nudgePending,
}: {
  candidate: UnifiedCandidate
  position: number
  applicant?: ApplicantCard
  onOpenPerson?: OpenJobWorkspacePersonHandler | OpenPersonMatchHandler
  onViewInPipeline?: (profileId: string) => void
  onNudge?: (applicationId: string) => void
  nudgePending?: boolean
}) {
  const rank = position + 1
  const name = candidateDisplayName(candidate)
  const qualityChip = matchQualityChip(candidateScore(candidate))
  const isInvited = applicant?.status === 'invited'
  const openHandler = resolveLeaderboardOpenPersonHandler(candidate, name, onOpenPerson)
  const interactive = typeof openHandler === 'function'

  return (
    <div
      className={cn(
        'relative flex flex-col gap-2 border-b border-border/60 py-2 last:border-b-0 sm:flex-row sm:items-center sm:justify-between',
        interactive && 'cursor-pointer rounded-md transition-colors hover:bg-muted/50',
      )}
      onClick={interactive ? openHandler : undefined}
    >
      {interactive ? (
        <CardOpenControl
          ariaLabel={`Open match analysis for ${name}`}
          onOpen={openHandler}
          rounded="md"
        />
      ) : null}
      <div className="pointer-events-none flex min-w-0 items-center gap-3">
        <Badge variant="secondary" className="tabular-nums">
          #{rank}
        </Badge>
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-foreground">{name}</p>
          <p className="truncate text-xs text-muted-foreground">
            {isInvited
              ? `Invited · awaiting application · ${calendarDaysSinceIso(applicant!.applied_at)}d`
              : applicant?.current_stage?.name ?? 'In pipeline'}
          </p>
        </div>
      </div>
      <div
        className="relative z-10 flex shrink-0 items-center gap-2 pointer-events-auto"
        onClick={(event) => event.stopPropagation()}
      >
        <MatchBandBadge band={qualityChip.band} />
        <span className="text-xs tabular-nums text-muted-foreground">{qualityChip.percentLabel}</span>
        {isInvited && applicant ? (
          <InvitedNudgeAction applicant={applicant} onNudge={onNudge} nudgePending={nudgePending} />
        ) : (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onViewInPipeline?.(candidate.profile_id)}
          >
            <Kanban className="mr-1.5 shrink-0" aria-hidden />
            View in pipeline
          </Button>
        )}
      </div>
    </div>
  )
}

export function SmartAnalysisLeaderboard({
  candidates,
  variant = 'standalone',
  onOpenPerson,
  applicantProfileById,
  applicantByProfileId,
  renderCandidateAction,
  onViewInPipeline,
  onNudge,
  nudgePending,
  className,
}: SmartAnalysisLeaderboardProps) {
  const jobReportCut = useMemo(
    () => (variant === 'job-report' ? leaderboardCandidates(candidates) : null),
    [variant, candidates],
  )
  const displayCandidates = jobReportCut ? jobReportCut.candidates : candidates.slice(0, 3)
  // Filler rows (below strong band) keep the leaderboard at 3+ rows; the caveat
  // states how many are genuinely strong so the floor never overstates the pool.
  const strongCaveat =
    jobReportCut && jobReportCut.strongCount < displayCandidates.length
      ? jobReportCut.strongCount === 0
        ? 'No strong matches yet — showing your best candidates.'
        : `Only ${jobReportCut.strongCount} strong ${jobReportCut.strongCount === 1 ? 'match' : 'matches'} yet.`
      : null

  if (displayCandidates.length === 0) return null

  const topThree = variant === 'job-report' ? displayCandidates.slice(0, 3) : displayCandidates
  const compactRows = variant === 'job-report' ? displayCandidates.slice(3) : []

  return (
    <section
      aria-label={variant === 'job-report' ? 'Top matches leaderboard' : 'Top candidates'}
      className={cn('space-y-3', className)}
    >
      <div className="flex flex-wrap items-baseline gap-2 border-b border-border pb-2">
        <h3 className="text-sm font-semibold text-foreground">
          {variant === 'job-report' ? 'Top matches' : 'Top candidates'}
        </h3>
        <span className="text-sm tabular-nums text-muted-foreground">{displayCandidates.length}</span>
        {strongCaveat ? (
          <span className="text-sm text-muted-foreground">· {strongCaveat}</span>
        ) : null}
      </div>
      <div className="space-y-3">
        {topThree.map((candidate, index) => (
          <LeaderboardCandidateRow
            key={candidate.profile_id}
            candidate={candidate}
            position={index}
            variant={variant}
            applicant={applicantByProfileId?.[candidate.profile_id]}
            onOpenPerson={onOpenPerson}
            applicantProfileById={applicantProfileById}
            renderCandidateAction={renderCandidateAction}
            onViewInPipeline={onViewInPipeline}
            onNudge={onNudge}
            nudgePending={nudgePending}
          />
        ))}
      </div>
      {compactRows.length > 0 ? (
        <div className="rounded-lg border border-border/60 px-3">
          {compactRows.map((candidate, index) => (
            <CompactLeaderboardRow
              key={candidate.profile_id}
              candidate={candidate}
              position={index + 3}
              applicant={applicantByProfileId?.[candidate.profile_id]}
              onOpenPerson={onOpenPerson}
              onViewInPipeline={onViewInPipeline}
              onNudge={onNudge}
              nudgePending={nudgePending}
            />
          ))}
        </div>
      ) : null}
    </section>
  )
}
